import type { VenueProp } from './world-content';

// Walking inside a place or a home: a grid over the floor with the props blocked, and the shortest way round
// them. Shared by the game (characters walking), the admin's Place Designer and the content checks (every spot
// must be reachable from the door), so a layout that traps someone can never be published.

const CELL = 0.25;
// what is always walked on, whatever its "flat" setting: a dance floor (people dance on it), rugs, paths, mats,
// a deck, a line on the ground
export const WALK_ON_SHAPES: ReadonlySet<string> = new Set([
  'rug',
  'path',
  'dancefloor',
  'yogamats',
  'pierdeck',
  'meridian',
]);
export const walkedOn = (prop: { shape?: string; flat?: boolean }) =>
  Boolean(prop.flat) || (prop.shape !== undefined && WALK_ON_SHAPES.has(prop.shape));
export type WalkGrid = { cols: number; rows: number; open: Uint8Array; w: number; d: number };
// footprint of a prop after turning it
const footOf = (prop: Pick<VenueProp, 'w' | 'd' | 'turn'>) =>
  prop.turn === 1 || prop.turn === 3 ? { w: prop.d, d: prop.w } : { w: prop.w, d: prop.d };

// `margin`: how close to a prop or a wall a character may walk (a small home needs a tighter one)
export function walkGrid(
  venue: {
    floor: { w: number; d: number };
    props: (Pick<VenueProp, 'x' | 'z' | 'w' | 'd' | 'turn' | 'flat'> & { shape?: string })[];
  },
  margin = 0.3,
): WalkGrid {
  const { w, d } = venue.floor;
  const cols = Math.floor(w / CELL),
    rows = Math.floor(d / CELL);
  const open = new Uint8Array(cols * rows).fill(1);
  for (let row = 0; row < rows; row += 1)
    for (let col = 0; col < cols; col += 1) {
      const x = -w / 2 + (col + 0.5) * CELL,
        z = -d / 2 + (row + 0.5) * CELL;
      // keep clear of the walls
      if (x < -w / 2 + margin || x > w / 2 - margin || z < -d / 2 + margin || z > d / 2 - margin) {
        open[row * cols + col] = 0;
        continue;
      }
      for (const prop of venue.props) {
        if (walkedOn(prop)) continue;
        const foot = footOf(prop);
        if (Math.abs(x - prop.x) < foot.w / 2 + margin && Math.abs(z - prop.z) < foot.d / 2 + margin) {
          open[row * cols + col] = 0;
          break;
        }
      }
    }
  return { cols, rows, open, w, d };
}

const cellOf = (grid: WalkGrid, x: number, z: number) => ({
  col: Math.min(grid.cols - 1, Math.max(0, Math.floor((x + grid.w / 2) / CELL))),
  row: Math.min(grid.rows - 1, Math.max(0, Math.floor((z + grid.d / 2) / CELL))),
});
const centreOf = (grid: WalkGrid, col: number, row: number) => ({
  x: -grid.w / 2 + (col + 0.5) * CELL,
  z: -grid.d / 2 + (row + 0.5) * CELL,
});

// the nearest open point to (x, z)
export function nearestOpen(grid: WalkGrid, x: number, z: number) {
  const start = cellOf(grid, x, z);
  let best: { col: number; row: number } | null = null,
    bestDistance = Infinity;
  for (let row = 0; row < grid.rows; row += 1)
    for (let col = 0; col < grid.cols; col += 1) {
      if (!grid.open[row * grid.cols + col]) continue;
      const distance = (col - start.col) ** 2 + (row - start.row) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = { col, row };
      }
    }
  return best ? centreOf(grid, best.col, best.row) : { x, z };
}

// a way from one point to another that goes round the props (corner points only); when the point cannot be
// reached (boxed in by furniture) the way leads as close to it as one can get; empty when already there
export function findPath(grid: WalkGrid, from: { x: number; z: number }, to: { x: number; z: number }) {
  const start = cellOf(grid, from.x, from.z);
  const goalPoint = nearestOpen(grid, to.x, to.z);
  const goal = cellOf(grid, goalPoint.x, goalPoint.z);
  let closest = -1,
    closestDistance = Infinity;
  const index = (col: number, row: number) => row * grid.cols + col;
  const came = new Int32Array(grid.cols * grid.rows).fill(-1);
  const startIndex = index(start.col, start.row);
  came[startIndex] = startIndex;
  const queue = [startIndex];
  const steps = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ];
  for (let head = 0; head < queue.length; head += 1) {
    const current = queue[head]!;
    if (current === index(goal.col, goal.row)) break;
    const col = current % grid.cols,
      row = Math.floor(current / grid.cols);
    const here = centreOf(grid, col, row);
    const distance = Math.hypot(here.x - to.x, here.z - to.z);
    if (distance < closestDistance) {
      closestDistance = distance;
      closest = current;
    }
    for (const [dc, dr] of steps) {
      const c = col + dc!,
        r = row + dr!;
      if (c < 0 || r < 0 || c >= grid.cols || r >= grid.rows) continue;
      const next = index(c, r);
      if (came[next] !== -1 || !grid.open[next]) continue;
      // no cutting corners past a prop
      if (dc && dr && (!grid.open[index(col + dc, row)] || !grid.open[index(col, row + dr)])) continue;
      came[next] = current;
      queue.push(next);
    }
  }
  let goalIndex = index(goal.col, goal.row);
  if (came[goalIndex] === -1) goalIndex = closest;
  if (goalIndex === -1 || goalIndex === startIndex) return [];
  const cells: number[] = [];
  for (let at = goalIndex; at !== startIndex; at = came[at]!) cells.push(at);
  cells.reverse();
  // keep only the turns, so the walk is smooth
  const points = cells.map((cell) => centreOf(grid, cell % grid.cols, Math.floor(cell / grid.cols)));
  return points.filter((point, i) => {
    const before = points[i - 1],
      after = points[i + 1];
    if (!before || !after) return true;
    return (point.x - before.x) * (after.z - point.z) !== (point.z - before.z) * (after.x - point.x);
  });
}

// the biggest stretch of floor one can walk round (furniture can box off a corner)
const mains = new WeakMap<WalkGrid, Uint8Array>();
function mainArea(grid: WalkGrid) {
  const known = mains.get(grid);
  if (known) return known;
  const region = new Int32Array(grid.cols * grid.rows).fill(-1);
  const sizes: number[] = [];
  for (let first = 0; first < region.length; first += 1) {
    if (!grid.open[first] || region[first] !== -1) continue;
    const id = sizes.length;
    const queue = [first];
    region[first] = id;
    for (let head = 0; head < queue.length; head += 1) {
      const cell = queue[head]!;
      const col = cell % grid.cols,
        row = Math.floor(cell / grid.cols);
      for (const [dc, dr] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ] as const) {
        const c = col + dc,
          r = row + dr;
        if (c < 0 || r < 0 || c >= grid.cols || r >= grid.rows) continue;
        const next = r * grid.cols + c;
        if (grid.open[next] && region[next] === -1) {
          region[next] = id;
          queue.push(next);
        }
      }
    }
    sizes.push(queue.length);
  }
  const biggest = sizes.indexOf(Math.max(...sizes, 0));
  const main = new Uint8Array(region.length);
  region.forEach((id, cell) => (main[cell] = id === biggest ? 1 : 0));
  mains.set(grid, main);
  return main;
}
// the nearest point to (x, z) on the main stretch of floor (where a character should start)
export function nearestMain(grid: WalkGrid, x: number, z: number) {
  const main = mainArea(grid);
  return nearestOpen({ ...grid, open: main }, x, z);
}

// somewhere open to wander to
export function randomOpen(grid: WalkGrid, random = Math.random) {
  for (let tries = 0; tries < 60; tries += 1) {
    const col = Math.floor(random() * grid.cols),
      row = Math.floor(random() * grid.rows);
    if (grid.open[row * grid.cols + col]) return centreOf(grid, col, row);
  }
  return nearestOpen(grid, 0, 0);
}

// the spots nobody can walk to from the door (the walk ends more than a step short of where the spot is)
export function unreachableSpots<Spot extends { id: string; x: number; z: number }>(interior: {
  floor: { w: number; d: number };
  props: (Pick<VenueProp, 'x' | 'z' | 'w' | 'd' | 'turn' | 'flat'> & { shape?: string })[];
  entrance: { x: number; z: number };
  spots: Spot[];
}): Spot[] {
  const grid = walkGrid(interior);
  const door = nearestOpen(grid, interior.entrance.x, interior.entrance.z);
  return interior.spots.filter((spot) => {
    const end = nearestOpen(grid, spot.x, spot.z);
    const last = findPath(grid, door, spot).at(-1) ?? door;
    return Math.hypot(last.x - end.x, last.z - end.z) > 0.01;
  });
}
